// src/hooks/useIdleTimeout.ts
// Session Timeout — auto-logout after a period of no user activity, on top
// of the existing 24h-access/7day-refresh token lifecycle (useTokenRefresh
// already handles that independently). Dispatching logout() is enough to
// end the session — PrivateRoute already redirects to /login once
// isAuthenticated flips false, same as every other logout path in the app.
import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { logout } from 'store/slices/authSlice';
import { showToast } from 'store/slices/toastSlice';
import { authService } from 'services/authService';

const DEFAULT_IDLE_MS = 24 * 60 * 60 * 1000; // 24 hours — matches the access/refresh token lifecycle
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;

export function useIdleTimeout(idleMs: number = DEFAULT_IDLE_MS): void {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((s: RootState) => s.auth);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleIdle = useCallback(() => {
    authService.logout().catch(() => { /* best-effort revoke; log out client-side regardless */ });
    dispatch(logout());
    dispatch(showToast('⏱️ Logged out due to inactivity'));
  }, [dispatch]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(handleIdle, idleMs);
  }, [handleIdle, idleMs]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    resetTimer();
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, resetTimer));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, resetTimer));
    };
  }, [isAuthenticated, resetTimer]);
}
